import { useFormContext } from 'react-hook-form';
import { Stack, Typography, Button, Chip } from '@optimus-web/core';
import { IconName } from '@optimus-web/icons';
import { TextKey } from '@mews-commander/enums';
import { lt } from '@mews/framework';
import { CompaniesFormFields, CompaniesFormValues } from '../../../../../schemas';
import { RoutingRulesFormFields } from '../../../utils';

interface CompanyChipsListProps {
    selectedCompanies: CompaniesFormValues;
    linkType: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency;
    searchValue: string;
    dataTestId: string;
}

export const CompanyChipsList = ({ selectedCompanies, linkType, searchValue, dataTestId }: CompanyChipsListProps) => {
    const { setValue } = useFormContext();

    const filteredCompaniesList = Object.entries(selectedCompanies)
        .filter(([_, company]) => company[linkType] && company.name.toLowerCase().includes(searchValue.toLowerCase()))
        .map(([id, company]) => ({ id, name: company.name }));

    const noMatchingItemsMessage =
        linkType === CompaniesFormFields.LinkAsCompany ? (
            <Typography textStyle="bodyMedium" as="div">
                {lt(TextKey.RoutingRulesFormCompaniesNoMatchingCompaniesMessage)}
            </Typography>
        ) : (
            <Typography textStyle="bodyMedium" as="div">
                {lt(TextKey.RoutingRulesFormCompaniesNoMatchingTravelAgenciesMessage)}
            </Typography>
        );

    const handleDeselectCompany = (
        id: string,
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency
    ) => {
        setValue(RoutingRulesFormFields.companies(), {
            ...selectedCompanies,
            [id]: {
                ...selectedCompanies[id],
                [key]: false,
            },
        });
    };

    const handleDeselectSearchedCompanies = (
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency
    ) => {
        const updatedCompanies = { ...selectedCompanies };
        filteredCompaniesList.forEach(({ id }) => {
            if (updatedCompanies[id]) {
                updatedCompanies[id] = { ...updatedCompanies[id], [key]: false };
            }
        });
        setValue(RoutingRulesFormFields.companies(), updatedCompanies);
    };

    return (
        <Stack vertical spacing="100" data-test-id={dataTestId}>
            <Stack distribution="space-between" itemAlignment="center">
                <Typography textStyle="bodyLargeStrong" as="div">
                    {linkType === CompaniesFormFields.LinkAsCompany
                        ? lt(TextKey.CompanyPlural)
                        : lt(TextKey.RoutingRulesFormCompaniesDialogSectionTitleTravelAgencies)}
                </Typography>
                {filteredCompaniesList.length !== 0 && (
                    <Button
                        variant="ghost"
                        icon={IconName.CrossClose}
                        onClick={() => handleDeselectSearchedCompanies(linkType)}
                        type="submit"
                    >
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt(TextKey.RoutingRulesFormCompaniesDialogClearAll)}
                        </Typography>
                    </Button>
                )}
            </Stack>
            {filteredCompaniesList.length === 0 ? (
                noMatchingItemsMessage
            ) : (
                <Stack spacing="50" wrap>
                    {filteredCompaniesList.map(({ id, name }) => (
                        <Chip key={id} value={id} onRemove={() => handleDeselectCompany(id, linkType)}>
                            {name}
                        </Chip>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
